'use client';

import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { useCheckoutStore } from '@/stores';
import { ROUTES } from '@/types/enums/Routes';
import type { Raffle } from '@/types/models/Raffle';
import { calculatePackTotal, isRaffleOnSale, logger, type PromotionOffer } from '@/utils';

type UseMainContentWithCheckoutProps = {
  raffle?: Raffle;
  packOffers: PromotionOffer[];
  promotionOffers: PromotionOffer[] | undefined;
  promotionOffersResolved?: boolean;
};

type UseMainContentWithCheckoutReturn = {
  ticketQuantity: number;
  totalValue: number;
  incrementQuantity: () => void;
  decrementQuantity: () => void;
  addQuantity: (amount: number) => void;
  handleComboClick: (pack: { quantity: number; percentage: number }) => void;
  handleChangeQuantity: (quantity: number) => void;
  resetQuantity: () => void;
  canIncrement: boolean;
  canDecrement: boolean;
  goToCheckout: () => void;
  hasQuantityError: boolean;
};

export const useMainContentWithCheckout = ({
  raffle,
  packOffers,
  promotionOffers,
  promotionOffersResolved,
}: UseMainContentWithCheckoutProps): UseMainContentWithCheckoutReturn => {
  const [ticketQuantity, setTicketQuantity] = useState(raffle?.initialTicketsAmount || 1);
  const [hasQuantityError, setHasQuantityError] = useState(false);
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const [lastClickedPackQuantity, setLastClickedPackQuantity] = useState<number | null>(
    null,
  );

  const router = useRouter();
  const {
    checkoutData,
    setPurchaseData,
    resetCheckout,
    updatePurchaseData,
    clearPurchaseData,
  } = useCheckoutStore();

  useEffect(() => {
    if (raffle?.initialTicketsAmount) {
      setTicketQuantity(raffle.initialTicketsAmount);
    }
  }, [raffle?.initialTicketsAmount]);

  useEffect(() => {
    // Limpa o checkout persistido se nao bater com a rifa atual.
    const storedRaffleId = checkoutData.purchaseData?.raffleId;
    if (!storedRaffleId || !raffle?.id) return;

    if (storedRaffleId !== raffle.id) {
      logger.warn('Clearing purchase data due to raffle mismatch', {
        source: 'home',
        storedRaffleId,
        currentRaffleId: raffle.id,
      });
      clearPurchaseData();
      return;
    }

    if (raffle.status && !isRaffleOnSale(raffle.status)) {
      logger.warn('Clearing purchase data due to raffle status', {
        source: 'home',
        raffleId: raffle.id,
        status: raffle.status,
      });
      clearPurchaseData();
    }
  }, [
    checkoutData.purchaseData?.raffleId,
    raffle?.id,
    raffle?.status,
    clearPurchaseData,
  ]);

  useEffect(() => {
    const ticketPrice = raffle?.ticketPrice || 0;
    localStorage.removeItem('highlightedRaffleId');
    localStorage.setItem('highlightedRaffleId', raffle?.id || '');

    const total = calculatePackTotal({
      offers: packOffers,
      ticketsAmount: ticketQuantity,
      unitPrice: ticketPrice,
    });

    setCalculatedTotal(total);
  }, [ticketQuantity, raffle, packOffers]);

  const totalValue = calculatedTotal;

  useEffect(() => {
    if (raffle) {
      updatePurchaseData({
        totalValue: calculatedTotal,
        ticketsAmount: ticketQuantity,
        ticketPrice: raffle.ticketPrice,
        raffleId: raffle.id,
        raffleName: raffle.name,
        prizeValue: raffle.prizeValue,
        drawDate: raffle.drawDate,
        isPromotionAvailable: raffle.isPromotionAvailable,
        promotionPercentage: raffle.promotionPercentage,
        promotionTitle: raffle.promotionTitle,
        promotionDescription: raffle.promotionDescription,
        promotionOffers,
        promotionOffersResolved: promotionOffersResolved ? true : undefined,
        minTicketsPerPurchase: raffle.minTicketsPerPurchase,
        maxTicketsPerPurchase: raffle.maxTicketsPerPurchase,
        acceptedOffers: undefined,
      } as Record<string, unknown>);
    }
  }, [
    calculatedTotal,
    ticketQuantity,
    raffle,
    updatePurchaseData,
    promotionOffers,
    promotionOffersResolved,
  ]);

  const incrementQuantity = useCallback(() => {
    setTicketQuantity((prev) => {
      const newQuantity = prev + 1;
      return newQuantity <= (raffle?.maxTicketsPerPurchase || 100) ? newQuantity : prev;
    });
  }, [raffle?.maxTicketsPerPurchase]);

  const decrementQuantity = useCallback(() => {
    setTicketQuantity((prev) => {
      const newQuantity = prev - 1;
      return newQuantity >= (raffle?.minTicketsPerPurchase || 1) ? newQuantity : prev;
    });
  }, [raffle?.minTicketsPerPurchase]);

  const addQuantity = useCallback(
    (amount: number) => {
      setTicketQuantity((prev) => {
        const newQuantity = prev + amount;
        const maxTickets = raffle?.maxTicketsPerPurchase || 100;
        const finalQuantity = newQuantity <= maxTickets ? newQuantity : maxTickets;
        return finalQuantity;
      });
    },
    [raffle?.maxTicketsPerPurchase],
  );

  const handleComboClick = useCallback(
    (pack: { quantity: number; percentage: number }) => {
      const maxTickets = raffle?.maxTicketsPerPurchase || 100;

      if (pack.quantity === lastClickedPackQuantity) {
        setTicketQuantity((prev) => {
          const newQuantity = prev + pack.quantity;
          return newQuantity <= maxTickets ? newQuantity : maxTickets;
        });
        setHasQuantityError(false);
      } else {
        setLastClickedPackQuantity(pack.quantity);
        if (pack.quantity <= maxTickets) {
          setTicketQuantity(pack.quantity);
        }
        setHasQuantityError(false);
      }
    },
    [raffle?.maxTicketsPerPurchase, lastClickedPackQuantity],
  );

  const resetQuantity = useCallback(() => {
    setTicketQuantity(raffle?.initialTicketsAmount || 1);
  }, [raffle?.initialTicketsAmount]);

  const goToCheckout = useCallback(() => {
    if (!raffle) return;
    if (!isRaffleOnSale(raffle.status)) return;

    resetCheckout();
    const purchaseData = {
      raffleId: raffle.id,
      ticketsAmount: ticketQuantity,
      ticketPrice: raffle.ticketPrice,
      totalValue: totalValue,
      raffleName: raffle.name,
      raffleTag: raffle.tag,
      drawDate: raffle.drawDate,
      prizeValue: raffle.prizeValue,
      isPromotionAvailable: raffle.isPromotionAvailable,
      promotionPercentage: raffle.promotionPercentage,
      promotionTicketsAmount: raffle.promotionTicketsAmount,
      promotionTitle: raffle.promotionTitle,
      promotionDescription: raffle.promotionDescription,
      promotionOffers,
      promotionOffersResolved: promotionOffersResolved ? true : undefined,
      minTicketsPerPurchase: raffle.minTicketsPerPurchase,
      maxTicketsPerPurchase: raffle.maxTicketsPerPurchase,
    };

    setPurchaseData(purchaseData);

    router.push(ROUTES.Enum['/checkout']);
  }, [
    raffle,
    ticketQuantity,
    totalValue,
    setPurchaseData,
    resetCheckout,
    router,
    promotionOffers,
    promotionOffersResolved,
  ]);

  const handleChangeQuantity = useCallback(
    (quantity: number) => {
      const min = raffle?.minTicketsPerPurchase || 1;
      const max = raffle?.maxTicketsPerPurchase || 999999;
      const quantityIsInvalid = quantity < min || quantity > max;
      setHasQuantityError(quantityIsInvalid);
      setTicketQuantity(quantity);
    },
    [raffle?.minTicketsPerPurchase, raffle?.maxTicketsPerPurchase],
  );

  return {
    ticketQuantity,
    totalValue,
    incrementQuantity,
    decrementQuantity,
    addQuantity,
    handleComboClick,
    handleChangeQuantity,
    resetQuantity,
    canIncrement: ticketQuantity < (raffle?.maxTicketsPerPurchase || 100),
    canDecrement: ticketQuantity > (raffle?.minTicketsPerPurchase || 1),
    goToCheckout,
    hasQuantityError,
  };
};
